import { useContext, useRef, useState } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { isResponseOk, useFetch } from "@swa-ui/fetch";

import { AuthContext } from "../../AuthProvider";
import { useUserInfo } from "../../UserInfoProvider";
import Queue from "./Queue";

const API_BASE_PATH = "/api/mfa/v1/mfa";
const API_TIMEOUT = 30000;
const CIAM_TIER_CODE = "080";

export const useMfaServices = (onBackgroundError) => {
  const aborted = useRef(false);
  const timedOut = useRef(false);
  const queue = useRef(new Queue({ autostart: true, concurrency: 1 }));
  const fetchOptions = {
    cachePolicy: "no-cache",
    onAbort: () => {
      aborted.current = true;
    },
    onTimeout: () => {
      timedOut.current = true;
    },
    timeout: API_TIMEOUT,
  };
  const { get, loading: initLoading, response: initResponse } = useFetch("", fetchOptions);
  const { loading: fetchLoading, post, response } = useFetch("", fetchOptions);
  const { post: backgroundPost, response: backgroundResponse } = useFetch("", fetchOptions);
  const {
    auth: { idToken },
    environmentTier,
  } = useContext(AuthContext);
  const { userInfo } = useUserInfo();
  const [loading, setLoading] = useState(false);
  const ids = useRef({});
  const mfaSettings = getBootstrapData("mfa-settings");

  return {
    getMfaDevices,
    loading: fetchLoading || initLoading || loading,
    resendMfaOtp,
    restartMfa,
    sendMfaOtp,
    validateMfaOtp,
  };

  async function getMfaDevices() {
    const initializeBody = await get(getInitializationPath());
    const callStatus = getCallStatus(initResponse, initializeBody);
    let devices = [];

    if (isResponseOk(initResponse)) {
      ids.current = { connectionId: initializeBody.connectionId, id: initializeBody.id };

      queue.current.push(async () => {
        const devicesBody = await backgroundPost(getPath(), getRequestBody({ id_token: idToken }));

        handleBackgroundResponse(
          { callStatus: getCallStatus(backgroundResponse, devicesBody) },
          () => {
            updateCurrentId(devicesBody);
          }
        );
      });

      devices = getPrimaryDeviceOptions();
    }

    return { callStatus, devices };
  }

  async function sendMfaOtp(deviceType) {
    queue.current.push(async () => {
      const body = await backgroundPost(getPath(), getRequestBody({ method: deviceType }));

      handleBackgroundResponse({ callStatus: getCallStatus(backgroundResponse, body) }, () => {
        updateCurrentId(body);
      });
    });

    await delay(1500);

    return { callStatus: { ok: true } };
  }

  async function resendMfaOtp() {
    const body = await post(getPath(), getRequestBody({ action: "resend" }));

    updateCurrentId(body);

    return { callStatus: getCallStatus(response, body) };
  }

  async function restartMfa() {
    const body = await post(getPath(), getRequestBody({ action: "restart" }));

    updateCurrentId(body);

    return { callStatus: getCallStatus(response, body) };
  }

  async function validateMfaOtp(otp) {
    return new Promise((resolve) => {
      setLoading(true);
      queue.current.push(async () => {
        const body = await post(
          getPath(),
          getRequestBody({
            action: "verify",
            otp,
          })
        );

        setLoading(false);

        updateCurrentId(body);

        resolve({ callStatus: getCallStatus(response, body) });
      });
    });
  }

  function delay(time) {
    setLoading(true);

    return new Promise((resolve) => {
      setTimeout(() => {
        setLoading(false);
        resolve();
      }, time);
    });
  }

  function getPrimaryDeviceOptions() {
    const primaryPhoneNumber = userInfo["customers.UserInformation.primaryPhoneNumber"];
    const primaryEmail = userInfo["customers.UserInformation.primaryEmail"];

    let deviceOptions = [];

    if (isDomesticPhone(primaryPhoneNumber) || isValidOptionValue(primaryEmail)) {
      deviceOptions = [
        ...getDeviceOption(primaryEmail, "EMAIL", isValidOptionValue, maskEmail),
        ...getDeviceOption(primaryPhoneNumber, "SMS", isDomesticPhone, maskPhoneNumber),
      ];
    } else {
      onBackgroundError?.("useMfaServices__NO_DEVICES_FOUND");
    }

    return deviceOptions;
  }

  function isValidOptionValue(contact) {
    const SAFE_MASKING_SIZE = 5;

    return contact && typeof contact === "string" && contact.length > SAFE_MASKING_SIZE;
  }

  function isDomesticPhone(phoneNumber) {
    return /^\d{10}$/.test(phoneNumber);
  }

  function getDeviceOption(value, deviceType, isValid, mask) {
    return isValid(value)
      ? [
          {
            deviceType,
            value: mask(value),
          },
        ]
      : [];
  }

  function maskPhoneNumber(phoneNumber) {
    let maskedPhoneNumber = phoneNumber;

    if (isValidOptionValue(phoneNumber)) {
      maskedPhoneNumber = `${phoneNumber.slice(0, -4).replace(/\d/g, "*")}${phoneNumber.slice(-4)}`;
    }

    return maskedPhoneNumber;
  }

  function maskEmail(email) {
    let maskedEmail = email;

    if (isValidOptionValue(email)) {
      const [user, domain] = email.split("@");
      const firstCharacter = user.slice(0, 1);
      const lastCharacter = user.slice(-1);
      const maskedUserWithoutFirstAndLast = user.slice(1, -1).replace(/\S/g, "*");

      maskedEmail = `${firstCharacter}${maskedUserWithoutFirstAndLast}${lastCharacter}@${domain}`;
    }

    return maskedEmail;
  }

  function getInitializationPath() {
    return `${getBasePath()}/as/authorize?client_id=${
      mfaSettings?.mfaClientIds?.[environmentTier]
    }&response_type=code&response_mode=pi.flow&scope=openid`;
  }

  function getPath() {
    return `${getBasePath()}/davinci/connections/${
      ids.current.connectionId
    }/capabilities/customHTMLTemplate`;
  }

  function getBasePath() {
    return `${API_BASE_PATH}/${mfaSettings?.mfaEnvIds?.[environmentTier]}`;
  }

  function getRequestBody(parameters) {
    return {
      eventName: "continue",
      id: ids.current.id,
      nextEvent: {
        constructType: "skEvent",
        eventName: "continue",
        eventType: "post",
        params: [],
        postProcess: {},
      },
      parameters: {
        buttonType: "form-submit",
        buttonValue: "submit",
        ...parameters,
      },
    };
  }

  function getCallStatus(serviceResponse, body) {
    const errorCode = isResponseOk(serviceResponse)
      ? undefined
      : (body && body.code?.match?.(/^\d{9}$/)?.[0]) ??
        `${serviceResponse.status ?? "000"}${CIAM_TIER_CODE}000`;

    return {
      aborted: aborted.current,
      errorCode,
      ok: !errorCode && !aborted.current,
      requestId: serviceResponse?.data?.requestId,
      timedOut: timedOut.current,
    };
  }

  function updateCurrentId(body) {
    ids.current.id = body && body.id;
  }

  function handleBackgroundResponse(
    responseToHandle,
    successCallback = undefined,
    errorCallback = undefined
  ) {
    const { callStatus, ...data } = responseToHandle;

    if (callStatus.ok) {
      successCallback && successCallback(data);
    } else {
      errorCallback ? errorCallback(callStatus) : onBackgroundError?.(callStatus);
    }
  }
};
